import { useEffect, useState } from 'react';

import { MenuItem, SelectChangeEvent } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import { ConditionalTooltip } from 'shared/components/Conditional';
import InputForm from 'shared/components/Input/InputForm';
import { APIErrorT } from 'shared/models/api';
import { FlowT } from 'shared/models/flows';
import { IPNetworkT } from 'shared/models/ip-networks';
import api from 'utils/api';

import { FormValues } from '../DeviceProfileManageData';

function ManageMulticastAddress() {
  const {
    setValue,
    getValues,
    control,
    formState: {
      errors,
    },
  } = useFormContext<FormValues>();
  const { flowId } = useParams() as RouterParams;
  const [isEnabled, setIsEnabled] = useState(getValues().isMulticast);
  const [hasNoAddresses, setHasNoAddresses] = useState('');
  const [multicastAddresses, setMulticastAddresses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const getFlowData = (callback: (data: FlowT) => void) => {
    api.flows.getOne(flowId as string)
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              setIsLoading(false);
            });
        } else {
          resp.json()
            .then(callback);
        }
      });
  };

  const getMulticastIPAddresses = (poolIdFromFlow: string) => {
    api.IPNetwork.getOne(poolIdFromFlow)
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              setIsLoading(false);
            });
        } else {
          resp.json()
            .then((ipNetworkData: IPNetworkT) => {
              const list = ipNetworkData.multicastInfos.map((multicastInfo) => multicastInfo.ip);
              const withoutDuplicates = list
                .filter((item: string, pos: number) => list.indexOf(item) === pos);
              if (!withoutDuplicates.length) {
                setIsEnabled(false);
                const message = 'No multicast addresses availables';
                setHasNoAddresses(message);
                toast.warning(message);
              }
              setMulticastAddresses(withoutDuplicates);
              setIsLoading(false);
            });
        }
      });
  };

  useEffect(() => {
    // Load data from API only the first time
    if (isEnabled && !multicastAddresses.length) {
      setIsLoading(true);
      getFlowData((flowData: FlowT) => getMulticastIPAddresses(flowData.poolId));
    }
  }, [isEnabled]);

  return (
    <>
      <ConditionalTooltip title={hasNoAddresses ?? undefined}>
        <InputForm.Checkbox
          sx={{ m: 1 }}
          label="Enable Multicast"
          name="isMulticast"
          control={control}
          disabled={!!hasNoAddresses}
          defaultChecked={getValues('isMulticast')}
          onChange={() => setIsEnabled(
            (currentValue) => {
              if (currentValue) {
                setValue('ipMulticastAddress', '');
              }
              return (!currentValue);
            },
          )}
          helpText="Allows multicast traffic on this device profile"
        />
      </ConditionalTooltip>
      <br />
      <InputForm.Select
        visible={isEnabled}
        label="IP Multicast Address"
        name="ipMulticastAddress"
        control={control}
        onChange={(event: SelectChangeEvent<any>) => (
          setValue('ipMulticastAddress', event.target.value)
        )}
        fullWidth
        sx={{ m: 1 }}
        isLoading={isLoading}
        hasError={!!errors.ipMulticastAddress}
      >
        {multicastAddresses.map((address) => (
          <MenuItem key={address} value={address}>
            { address }
          </MenuItem>
        ))}
      </InputForm.Select>
    </>
  );
}

export default ManageMulticastAddress;
